import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import '../styles/globals.css';
import Layout from '../components/Layout';
import CallModal from '../components/CallModal';
import * as gtag from '../lib/gtag';
import { initializeAds } from '../lib/ads';
import { AuthProvider } from '../utils/authClient';
import { disableErrorOverlay } from '../utils/errorHandling';
import ErrorBoundary from '../components/ErrorBoundary';
import config from '../config'; // Import config to get siteUrl

// Only run on server side, and not during build
if (typeof window === 'undefined' && process.env.IS_BUILDING !== 'true' && process.env.RUN_STARTUP_CHECKS !== 'false') {
  // Import startup checks only on server side to avoid bundling with client code
  const { runStartupChecks } = require('../lib/startup-checks.js');
  
  // Run immediately when this module is imported
  (async () => {
    try {
      console.log('Running startup checks on server initialization...');
      await runStartupChecks();
      console.log('Startup checks completed successfully.');
    } catch (error) {
      console.error('Startup checks failed:', error);
    }
  })();
}

// Stealth Mode Maintenance Component
const MaintenancePage = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-blue-50">
      <Head>
        <title>Water Damage Direct - UAT Environment</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="description" content="Water Damage Direct UAT environment is currently in maintenance mode. This environment is for testing purposes only." />
      </Head>
      <div className="text-center p-8 max-w-lg bg-white shadow-lg rounded-lg">
        <h1 className="text-3xl font-bold text-blue-800 mb-4">UAT Environment</h1>
        <div className="mb-6">
          <svg className="w-24 h-24 mx-auto text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
          </svg>
        </div>
        <p className="text-gray-700 mb-4">
          This is a UAT (User Acceptance Testing) environment for Water Damage Direct.
        </p>
        <p className="text-gray-500 text-sm mt-4">
          For internal testing purposes only - not accessible to the public.
        </p>
      </div>
    </div>
  );
};

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const [isStealthMode, setIsStealthMode] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [currentContractor, setCurrentContractor] = useState(null);
  
  // Initialize stealth mode state
  useEffect(() => {
    // In Next.js, we need to use getConfig() to access publicRuntimeConfig values
    const getConfig = require('next/config').default;
    const { publicRuntimeConfig } = getConfig() || {};
    
    // Get stealth mode from window variable - this is the most reliable in container environments
    const stealth_window_var = typeof window !== 'undefined' && 
                               window.__NEXT_DATA__ && 
                               window.__NEXT_DATA__.props && 
                               window.__NEXT_DATA__.props.pageProps && 
                               window.__NEXT_DATA__.props.pageProps.__STEALTH_MODE;
    
    // Simplify to just use STEALTH_MODE via window variable or publicRuntimeConfig
    const stealthMode = 
      stealth_window_var === 'true' ||
      (publicRuntimeConfig && publicRuntimeConfig.stealthMode);
    
    /* Original log:
    console.log('STEALTH MODE CHECK:', { 
      stealth_window_var,
      publicRuntimeConfig: publicRuntimeConfig,
      stealthModeFromConfig: publicRuntimeConfig?.stealthMode,
      finalStealthMode: stealthMode
    });
    */
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('STEALTH MODE CHECK:', { 
        stealth_window_var,
        publicRuntimeConfig: publicRuntimeConfig,
        stealthModeFromConfig: publicRuntimeConfig?.stealthMode,
        finalStealthMode: stealthMode
      });
    }
    
    setIsStealthMode(stealthMode);
  }, []);
  
  // Initialize call modal with mode support
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.openCallModal = (contractor, mode = 'call') => {
        setCurrentContractor({...contractor, _callMode: mode});
        setCallModalOpen(true);
      };
    }
  }, []);
  
  // Track page views when route changes
  useEffect(() => {
    // Skip tracking during development to avoid noise in GA
    if (process.env.NODE_ENV !== 'production') return;
    
    const handleRouteChange = (url) => {
      gtag.pageview(url);
    };
    
    // Track initial page load
    handleRouteChange(router.asPath);
    
    // Track route changes
    router.events.on('routeChangeComplete', handleRouteChange);
    
    // Clean up event listener
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, router.asPath]);
  
  // Add cache busting for API calls with fetch
  useEffect(() => {
    // Override fetch to add cache busting to API calls
    const originalFetch = window.fetch;
    window.fetch = function(url, options) {
      if (typeof url === 'string' && url.startsWith('/api')) {
        // Add cache busting parameter to API calls
        const separator = url.includes('?') ? '&' : '?';
        url = `${url}${separator}_cb=${Date.now()}`;
      }
      return originalFetch(url, options);
    };
    
    return () => {
      // Restore original fetch when component unmounts
      window.fetch = originalFetch;
    };
  }, []);
  
  // Initialize Google AdSense
  useEffect(() => {
    if (typeof window !== 'undefined') {
      initializeAds();
    }
  }, []);
  
  // Disable error overlay in development to prevent auto-refresh on errors
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      disableErrorOverlay();
      console.log('🛑 Auto-refresh disabled for error debugging');
    }
  }, []);
  
  // Debug info 
  if (typeof window !== 'undefined') {
    /* Original log:
    console.log('STEALTH STATUS:', {
      hostname: window.location.hostname,
      isStealthMode,
      usingMiddlewareAuth: true
    });
    */
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('STEALTH STATUS:', {
        hostname: window.location.hostname,
        isStealthMode,
        usingMiddlewareAuth: true // Assuming this context is still relevant
      });
    }
  }

  const canonicalUrl = `${config.siteUrl}${router.asPath.split('?')[0]}`;
  
  // 1. Defaults
  let title = 'Water Damage Direct | Find Local Restoration Services 24/7';
  let description = 'Connect with certified water damage restoration professionals for emergency cleanup, flood repair, and mold remediation. Fast, reliable service near you.';
  let ogImage = 'https://www.waterdamagedirect.com/images/og-image.jpg';

  // 2. If the page component provides static meta (preferred)
  if (Component.meta) {
    title = Component.meta.title || title;
    description = Component.meta.description || description;
    ogImage = Component.meta.ogImage || ogImage;
  }
  // 3. Else, if pageProps has meta (from getServerSideProps/getStaticProps)
  else if (pageProps.title || pageProps.description) {
    title = pageProps.title || title;
    description = pageProps.description || description;
    ogImage = pageProps.ogImage || ogImage;
  }

  // If in stealth mode, render maintenance page
  if (isStealthMode && !router.pathname.startsWith('/auth/stealth-login')) {
    return <MaintenancePage />;
  }

  return (
    <ErrorBoundary>
      <Head>
        <title key="title">{title}</title>
        <meta key="description" name="description" content={description} />
        <meta key="og:title" property="og:title" content={title} />
        <meta key="og:description" property="og:description" content={description} />
        <meta key="og:image" property="og:image" content={ogImage} />
        <link rel="canonical" key="canonical" href={canonicalUrl} />
      </Head>
      <AuthProvider>
            <Layout>
              <Component {...pageProps} />
          {callModalOpen && (
        <CallModal
          isOpen={callModalOpen}
          onClose={() => setCallModalOpen(false)}
          contractor={currentContractor}
              mode={currentContractor?._callMode || 'call'}
        />
      )}
        </Layout>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default MyApp;